import { ScheduledSetting as ShowSetting, ScheduleMarker, ScheduleDraftStatus, useScheduledSettings } from './Scheduling/ScheduledSettings';
import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useRef } from 'react';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { IconButton } from '@material-ui/core';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core';

import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { EnableDisableSelect } from '../FormWidgets/Select.js';
import Desc from '../FormWidgets/Description';
import { HandleAction, isZoned } from '../Utils/utils.js';

import { useTabSettings } from './settingFunctions';
import { co2TemperatureSettings } from '../Utils/co2_utils';
import { SettingActions } from '../components';
import LightChannelTabs from './LightChannelTabs';
import AdvancedLightChannels from './AdvancedLightChannels';
import LegacyLightPreview from './Lights/LegacyLightPreview';
import { isPendingLightCatalog, lightCatalogNeedsRecovery } from '../Utils/light_channels_transaction';
import {
  hasLightChannelCapability,
  lightStatusUnknown,
  supportsLightChannels
} from '../Utils/light_profiles';
import {
  getLightCopyZoneSettings,
  prepareLightCopyZoneSettings,
  restoreLightCopyZoneSettings,
  saveLightCopyZoneSettings
} from '../Utils/light_copy_zone_settings';

const useStyles = makeStyles((theme) => ({
  lightTabsBar: {
    margin: '24px 24px 0',
    width: 'calc(100% - 48px)',
    [theme.breakpoints.down('xs')]: {
      margin: '10px 4px 0',
      minHeight: 48,
      width: 'calc(100% - 8px)'
    }
  }
}));

export default function LightsSettings({ ...other }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const channelControls = useRef(null);
  // Ordinary zone drafts may still need recovery while the global catalog is
  // being saved. Their Restore/Discard choice must not reset that submission.
  const channelRecovery = { current: {
    restoreTempSettings: () => {
      if (!isPendingLightCatalog(controller)) channelControls.current?.restoreTempSettings();
    },
    reset: () => {
      if (!isPendingLightCatalog(controller)) channelControls.current?.reset();
    }
  } };
  const {
    t,
    params,
    stateManager,
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values: ordinaryValues,
    setValues,
    controls: ordinaryControls,
    restoreControls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog,
    tab_id
  } = useTabSettings(null, [], undefined, {
    controls: channelRecovery,
    ready: supportsLightChannels,
    differs: (controller) => supportsLightChannels(controller) && lightCatalogNeedsRecovery(controller)
  });
  const schedule = useScheduledSettings(controller, parent, 'lights', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;
  const lightingSnapshot = controllerSnapshot;
  const settingsSnapshot = parentSnapshot.settings;
  const channelsSupported = supportsLightChannels(lightingSnapshot);
  const channelCapability = hasLightChannelCapability(lightingSnapshot);
  const channelsPending = channelCapability && !channelsSupported;
  const lightInfo = lightingSnapshot.settings.light_channels_info;
  const activeZone = Number(parent.zone_id);
  const applyCopiedZoneLights = (catalog, restore = false) => {
    const copied = catalog?._zone_lights?.[activeZone];
    if (!copied) return;
    const next = restore
      ? restoreLightCopyZoneSettings(catalog, activeZone, parent.temp_settings.lights)
      : _.cloneDeep(copied);
    // Use the ordinary draft and controls for the displayed zone. Keeping the
    // complete replacement also supersedes any pre-copy edits to these fields.
    const entries = Object.entries(next);
    if (entries.length)
      ordinaryControls.setValue(
        entries.map(([key]) => key),
        entries.map(([, value]) => value)
      );
    ordinaryControls.saveTempSettings(null, next);
  };
  const saveLights = () => {
    if (schedule.saving || controller.light_submission?.status === 'pending') return;
    const channels = channelControls.current;
    let catalogSent = false;
    let ordinaryReady = !channelsPending;
    if (ordinaryReady && channels?.hasChanges()) {
      ordinaryReady = channels.canSave();
      if (ordinaryReady) {
        ordinaryReady = saveLightCopyZoneSettings(controller, channels.getCatalog(), stateManager, activeZone);
        if (ordinaryReady) {
          catalogSent = channels.save();
          ordinaryReady = catalogSent;
        }
        else enqueueSnackbar(t('lighting_save_failed'), { variant: 'error' });
      }
    }
    // Copied ordinary settings and their catalog retain their save ordering.
    // A blocked lighting copy must not swallow an independent recipe edit.
    if (!ordinaryReady) {
      if (schedule.recipe.dirty) schedule.recipe.save();
    } else if (catalogSent) controls.saveChangedSettings();
    else controls.saveSettings();
  };
  const resetLights = () => {
    channelControls.current?.reset();
    controls.resetSettings();
  };
  const read_access_climate = controllerSnapshot.access_rights.climate.read;
  const co2Temperature = co2TemperatureSettings(controllerSnapshot, parentSnapshot);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);
  const write_access = controllerSnapshot.access_rights.lights.write && !channelsPending;
  const canSave = schedule.canSave &&
    (!channelsPending || (schedule.recipe.writable && schedule.recipe.dirty));

  const callAction = (type, value) => {
    const zone_id = isZoned(controller) ? parent.id : undefined;
    HandleAction(stateManager.wsManager, controller, type, value, zone_id);
  };

  // Tab handling for lights Settings
  const subCnt = Math.max(1, Number(values?.lights_sub_zones_active_cnt ?? 1));
  const lightIds = Array.from({ length: subCnt }, (_, i) => i + 1); // [1..subCnt]
  const renderLegacyPreview = (id = 1) => (
    <LegacyLightPreview
      timing={lightingSnapshot.access_rights.timing?.read ? settingsSnapshot.timing : null}
      phase={schedule.recipe.context?.metadata.schedule_day_night_enabled ? schedule.recipe.context.phase : null}
      minimum={values.lights_min}
      maximum={id === 1 ? values.lights_max : values[`lights_max_${id}`]}
      sunriseDuration={values.light_sunrise_duration}
    />
  );

  // Min of active lights_max values (LIGHTS 1..subCnt)
  const candidateMax = [
    values.lights_max,
    values.lights_max_2,
    values.lights_max_3,
    values.lights_max_4
  ]
    .slice(0, subCnt)
    .filter((v) => typeof v === 'number');
  const MinLightsMax = candidateMax.length ? Math.min(...candidateMax) : values.lights_max;
  // Channel lighting uses a proportional thermal factor, independent of the
  // legacy output minimum/maximum that are no longer edited in this panel.
  // Revision zero still runs the legacy program until the first durable catalog
  // save. Keep its bounds until readback confirms activation, independent of the
  // ordering of the separate catalog and ordinary-settings messages.
  const channelThermal =
    channelCapability && Number(lightingSnapshot.settings.light_channels?.revision) > 0;
  const thermalOff = channelThermal ? 9 : values.lights_min - 1;
  const thermalMaximum = channelThermal ? 100 : MinLightsMax;
  const thermalHelp = channelThermal
    ? { brief: t('lighting_thermal_factor_title'), content: t('lighting_thermal_factor_help') }
    : undefined;

  return (
    <React.Fragment>
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={restoreControls}
      />

      {canSave && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={
            schedule.saving || lightingSnapshot.light_submission?.status === 'pending' || connectionStatus !== 'good'
          }
          onReset={resetLights}
          onSave={saveLights}
        />
      )}

      {channelsPending && (
        <Alert severity={lightInfo.storage_state === 2 ? 'error' : 'info'}>
          {t(
            lightStatusUnknown(lightInfo)
              ? 'lighting_unknown_status'
              : lightInfo.zone_faults?.some((fault) => fault & 8)
                ? 'lighting_core_unavailable'
                : lightInfo.storage_state === 2
                  ? 'lighting_storage_unavailable'
                  : 'lighting_wait_readback'
          )}
        </Alert>
      )}

      {channelsSupported && (
        <AdvancedLightChannels
          schedule={schedule}
          simpleSettings={ordinaryValues}
          ref={channelControls}
          controller={controller}
          parent={parent}
          stateManager={stateManager}
          getZoneLights={(catalog, zone) =>
            getLightCopyZoneSettings(controller, catalog, zone, activeZone, ordinaryValues)
          }
          onZoneLightsCopied={(catalog, targetZone) => {
            if (Number(targetZone) === activeZone) applyCopiedZoneLights(catalog);
          }}
          onZoneLightsRestored={(catalog) => applyCopiedZoneLights(catalog, true)}
          prepareCatalogForSave={(catalog) =>
            prepareLightCopyZoneSettings(catalog, activeZone, ordinaryValues, controller)
          }
        />
      )}
      {/* Tabbed LIGHTS */}
      {isZoned(controllerSnapshot) && !channelCapability && (
        <SettingGroupGrid fullWidth={true}>
          <LightChannelTabs
            ids={lightIds}
            t={t}
            idPrefix="light"
            controller={controllerSnapshot}
            controls={controls}
            appBarClassName={classes.lightTabsBar}
            getFieldId={(id) => (id === 1 ? 'lights_max' : `lights_max_${id}`)}
            getValue={(id) => (id === 1 ? values.lights_max : values[`lights_max_${id}`])}
            getControllerValue={(id) =>
              id === 1
                ? parentSnapshot.settings.lights.lights_max
                : parentSnapshot.settings.lights[`lights_max_${id}`]
            }
            getMin={() => values.lights_min}
            disabled={(id) => schedule.disabled(id === 1 ? 'lights_max' : `lights_max_${id}`, undefined, !write_access)}
            renderInfo={(id) => <ScheduleMarker target={schedule(id === 1 ? 'lights_max' : `lights_max_${id}`)} />}
            renderPreview={renderLegacyPreview}
          />
        </SettingGroupGrid>
      )}

      {!channelCapability && (
        <SettingGroupGrid controller={controllerSnapshot} id={'lights_single'}>
          {!isZoned(controllerSnapshot) && (
            <ShowSetting
              schedule={schedule}
              id="lights_max"
              controller={controllerSnapshot}
              cur_value={values.lights_max}
              controller_value={parentSnapshot.settings.lights.lights_max}
              min={values.lights_min}
              controls={controls}
              disabled={schedule.disabled('lights_max', undefined, !write_access)}
            />
          )}
          <ShowSetting
            id="lights_min"
            controller={controllerSnapshot}
            cur_value={values.lights_min}
            controller_value={parentSnapshot.settings.lights.lights_min}
            max={isZoned(controllerSnapshot) ? MinLightsMax : values.lights_max}
            controls={controls}
            disabled={schedule.disabled('lights_min', undefined, !write_access)}
          />
        </SettingGroupGrid>
      )}

      {!channelCapability && !isZoned(controllerSnapshot) && renderLegacyPreview()}

      <SettingGroupGrid controller={controllerSnapshot} id={'lights_sunrise_duration'}>
        <ShowSetting
          id="light_sunrise_duration"
          controller={controllerSnapshot}
          cur_value={values.light_sunrise_duration}
          controller_value={parentSnapshot.settings.lights.light_sunrise_duration}
          controls={controls}
          disabled={schedule.disabled('light_sunrise_duration', undefined, !write_access)}
        />
      </SettingGroupGrid>

      <SettingGroupGrid controller={controllerSnapshot} id={'lights_height_adjustment_hl'}>
        <ValueBox controller={controllerSnapshot} id={'lights_height_adjustment_single'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_single" />
          </Typography>
          <div style={{ display: 'flex', gap: 21 }}>
            {[1, 2, 3, 4].map((num) => (
              <div
                key={num}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <ActionButton
                  title={connectionDisabledReason}
                  component={IconButton}
                  disabled={connectionStatus !== 'good'}
                  onClick={() => callAction(`action_lights_height_adjustment_up_${num}`)}
                >
                  <KeyboardArrowUpIcon />
                </ActionButton>
                <ActionButton
                  title={connectionDisabledReason}
                  component={IconButton}
                  disabled={connectionStatus !== 'good'}
                  onClick={() => callAction(`action_lights_height_adjustment_down_${num}`)}
                >
                  <KeyboardArrowDownIcon />
                </ActionButton>
                <Typography variant="caption" color="primary">
                  <Desc id={`${num}`} />
                </Typography>
              </div>
            ))}
          </div>
        </ValueBox>
        <ValueBox controller={controllerSnapshot} id={'lights_height_adjustment_global'}>
          <Typography gutterBottom>
            <Desc id="lights_height_adjustment_global" />
          </Typography>
          <ActionButton
            title={connectionDisabledReason}
            component={IconButton}
            disabled={connectionStatus !== 'good'}
            onClick={() => callAction('action_lights_height_adjustment_up_0')}
          >
            <KeyboardArrowUpIcon />
          </ActionButton>
          <br />
          <ActionButton
            title={connectionDisabledReason}
            component={IconButton}
            disabled={connectionStatus !== 'good'}
            onClick={() => callAction('action_lights_height_adjustment_down_0')}
          >
            <KeyboardArrowDownIcon />
          </ActionButton>
        </ValueBox>
      </SettingGroupGrid>

      <SettingGroupGrid controller={controllerSnapshot} id={'over_temperature_hl'}>
        <ValueBox controller={controllerSnapshot} id={'lights_dim_over_temp_enable'}>
          <Typography gutterBottom>
            <Desc id="lights_dim_over_temp_enable" />
          </Typography>
          <EnableDisableSelect controls={controls}
            value={values.lights_dim_over_temp_enable}
            controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_enable}
            name="lights_dim_over_temp_enable"
            onChange={(event, value) => {
              controls.setValue('lights_dim_over_temp_enable', event.target.value);
            }}
            disabled={!write_access}
          />
        </ValueBox>
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_temp_day"
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_temp_day}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_temp_day}
          min={read_access_climate ? parentSnapshot.settings.climate.temp_sp_day / 10 + 1 : 0}
          controls={controls}
          disabled={schedule.disabled('lights_dim_over_temp_temp_day', undefined, !write_access)}
          type="float"
        />
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_temp_co2"
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_temp_co2}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_temp_co2}
          min={co2Temperature.readable ? co2Temperature.value / 10 + 1 : 0}
          controls={controls}
          disabled={schedule.disabled('lights_dim_over_temp_temp_co2', undefined, !write_access)}
          type="float"
        />
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_reduct_ph1"
          helpOverride={thermalHelp}
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_reduct_ph1}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_reduct_ph1}
          min={thermalOff}
          max={thermalMaximum}
          controls={controls}
          disabled={!write_access}
          off_at={thermalOff}
        />
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_reduct_ph2"
          helpOverride={thermalHelp}
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_reduct_ph2}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_reduct_ph2}
          min={thermalOff}
          max={values.lights_dim_over_temp_reduct_ph1}
          controls={controls}
          disabled={!write_access}
          off_at={thermalOff}
        />
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_reduct_ph3"
          helpOverride={thermalHelp}
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_reduct_ph3}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_reduct_ph3}
          min={thermalOff}
          max={values.lights_dim_over_temp_reduct_ph2}
          controls={controls}
          disabled={!write_access}
          off_at={thermalOff}
        />
        <ShowSetting
          schedule={schedule}
          id="lights_dim_over_temp_reduct_wait_minute"
          controller={controllerSnapshot}
          cur_value={values.lights_dim_over_temp_reduct_wait_minute}
          controller_value={parentSnapshot.settings.lights.lights_dim_over_temp_reduct_wait_minute}
          controls={controls}
          disabled={schedule.disabled('lights_dim_over_temp_reduct_wait_minute', undefined, !write_access)}
        />
      </SettingGroupGrid>

      {canSave && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={
            schedule.saving || lightingSnapshot.light_submission?.status === 'pending' || connectionStatus !== 'good'
          }
          onReset={resetLights}
          onSave={saveLights}
        />
      )}
    </React.Fragment>
  );
}
